import { Tooltip } from "@/base";
import { isPrimitive } from "@/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, type TextAreaProps, Textarea, cn } from "@heroui/react";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";
import {
    type KeyboardEvent,
    isValidElement,
    useCallback,
    useEffect,
    useState,
} from "react";
import {
    Controller,
    type FieldValues,
    type Path,
    type UseFieldArrayRemove,
    type UseFormReturn,
} from "react-hook-form";

export interface TextArrayInputItemProps {
    isDisabled?: TextAreaProps["isDisabled"];
    originalIndex: number;
    remove?: UseFieldArrayRemove;
    label?: string;
    hideItemLabel?: boolean;
    isProtected?: boolean;
    isAutofocusing?: boolean;
    type?: TextAreaProps["type"];
    isRequired?: TextAreaProps["isRequired"];
    startContent?: TextAreaProps["startContent"];
    endContent?: TextAreaProps["endContent"];
    isInvalid?: TextAreaProps["isInvalid"];
    errorMessage?: TextAreaProps["errorMessage"];
    value?: TextAreaProps["value"];
    size?: TextAreaProps["size"];
    name?: TextAreaProps["name"];
    preventNewLine?: boolean;
    isViewOnly?: boolean;
    onChange?: TextAreaProps["onChange"];
    onKeyDown?: TextAreaProps["onKeyDown"];
    onBlur?: TextAreaProps["onBlur"];
    maxLength?: number;
}

export type ControlledTextArrayInputItemProps<T> = TextArrayInputItemProps & {
    fieldName: Path<T>;
    valuePropertyName: string;
    formController: UseFormReturn<T>;
};

const InputItem = ({
    remove,
    originalIndex,
    isDisabled,
    isProtected,
    endContent,
    label,
    hideItemLabel,
    value,
    isViewOnly,
    maxLength,
    ...rest
}: TextArrayInputItemProps) => {
    const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setIsCopyTooltipOpen(true);
            setTimeout(() => setIsCopyTooltipOpen(false), 1000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    }, [value]);

    return (
        <div className="relative flex w-full items-center gap-2">
            <Textarea
                {...rest}
                className={cn(isViewOnly && "opacity-70")}
                value={value}
                label={!hideItemLabel && label}
                aria-label={label}
                isDisabled={isDisabled || isViewOnly}
                fullWidth={true}
                minRows={1}
                maxLength={maxLength}
                classNames={{
                    innerWrapper: "items-center",
                }}
            />
            {remove && !isViewOnly && (
                <Button
                    onPress={() => remove(originalIndex)}
                    size="sm"
                    variant="light"
                    color="danger"
                    isIconOnly={true}
                    isDisabled={isDisabled || isProtected}
                >
                    <IconTrash stroke={1.5} size={20} />
                </Button>
            )}
            {value && isViewOnly && (
                <Tooltip
                    content="Kopiert"
                    placement="top-end"
                    closeDelay={2000}
                    isOpen={isCopyTooltipOpen}
                >
                    <button
                        type="button"
                        className="absolute top-0 left-0 h-full w-full"
                        onClick={handleCopy}
                    />
                </Tooltip>
            )}
        </div>
    );
};

/**
 * We use a controlled component here since react hook form was scrolling
 * newly created inputs into focus when using register directly
 */
const ControlledInputItem = <T extends FieldValues>({
    originalIndex,
    fieldName,
    valuePropertyName,
    formController,
    preventNewLine = true,
    ...rest
}: {
    fieldName: Path<T>;
    valuePropertyName: string;
    formController: UseFormReturn<T>;
} & TextArrayInputItemProps) => {
    useEffect(() => {
        formController.register(
            `${fieldName}.${originalIndex}.${valuePropertyName}` as Path<T>,
        );
    }, [formController, fieldName, originalIndex, valuePropertyName]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (preventNewLine && event.key === "Enter") {
                event.preventDefault(); // Prevents new line
            }
        },
        [preventNewLine],
    );

    return (
        <Controller
            name={
                `${fieldName}.${originalIndex}.${valuePropertyName}` as Path<T>
            }
            control={formController.control}
            render={({ field: { onChange, onBlur, value, name } }) => {
                return (
                    <InputItem
                        name={name}
                        value={value}
                        onChange={onChange}
                        onKeyDown={handleKeyDown}
                        onBlur={onBlur}
                        originalIndex={originalIndex}
                        {...rest}
                    />
                );
            }}
        />
    );
};

export const BaseTextArrayFieldItem = <T extends FieldValues>({
    id,
    fieldName,
    originalIndex,
    isDisabled,
    formController,
    valuePropertyName,
    disableDnd,
    errorMessage,
    hideItemLabel,
    hideItem,
    isViewOnly,
    inputItem,
    controlledInputItem,
    ...rest
}: {
    id: string;
    formController?: UseFormReturn<T>;
    fieldName: Path<T>;
    originalIndex: number;
    valuePropertyName: string;
    disableDnd?: boolean;
    hideItem?: boolean;
    inputItem?: React.ComponentType<TextArrayInputItemProps>;
    controlledInputItem?: React.ComponentType<
        ControlledTextArrayInputItemProps<T>
    >;
} & TextArrayInputItemProps) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
        useSortable({ id, disabled: disableDnd || isViewOnly || isDisabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: "all 0.2s",
        opacity: isDragging ? 0 : 1,
    };

    const InputComponent = inputItem || InputItem;
    const ControlledInputComponent = controlledInputItem || ControlledInputItem;

    return (
        <div
            className={cn(
                hideItem && "hidden",
                isDisabled && "pointer-events-none",
            )}
        >
            <div
                className="flex flex-row items-center gap-2"
                ref={setNodeRef}
                style={style}
                {...attributes}
            >
                {!(disableDnd || isViewOnly) && (
                    <div {...listeners} className="self-start py-2.5">
                        <IconGripVertical
                            stroke={1.5}
                            size={20}
                            className={cn(
                                "cursor-grabbing",
                                isDisabled &&
                                    "pointer-events-none cursor-default opacity-50",
                            )}
                        />
                    </div>
                )}
                {formController ? (
                    <ControlledInputComponent
                        fieldName={fieldName}
                        originalIndex={originalIndex}
                        formController={formController as UseFormReturn<T>}
                        valuePropertyName={valuePropertyName}
                        hideItemLabel={hideItemLabel}
                        isViewOnly={isViewOnly}
                        {...rest}
                    />
                ) : (
                    <InputComponent
                        originalIndex={originalIndex}
                        isViewOnly={isViewOnly}
                        isDisabled={isDisabled}
                        {...rest}
                    />
                )}
            </div>
            {errorMessage &&
                (isValidElement(errorMessage) || isPrimitive(errorMessage)) && (
                    <div
                        className={cn(
                            "mt-0.5 text-danger text-tiny",
                            !disableDnd && "ml-9",
                        )}
                    >
                        {errorMessage}
                    </div>
                )}
        </div>
    );
};
